import { useState, useRef } from 'react';
import Modal from "@modalSrc/Modal"; // Adjust path to your generic Modal
import ModalFormFields from "@modalSrc/ModalFormFields"; // Import for fields rendering
import { useEffect } from 'react';
import { IoTrashOutline } from 'react-icons/io5';
import ConfirmModal from './ConfirmModal';
import JobDescriptionModal from './JobDescriptionModal';
import EditSiteModal from './EditSiteModal';
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import { FaPlusSquare, FaFileUpload, FaFileDownload } from "react-icons/fa";
import { MdCheckBox, MdCheckBoxOutlineBlank, MdCameraswitch } from "react-icons/md";
import { DateTime } from 'luxon';
import Helpers from "@/Helpers";
import Enums from "@/enums";
import { useI18n } from "@/i18n/I18nProvider";
import { readPermIdsFromSession } from "@/i18n/sessionUser";
import { fetchCompanyJobsTabMerged } from "@/utils/companyJobsTabApi";

const JOB_DESCRIPTION_FILE_EXTENSIONS = [".pdf", ".docx", ".txt"];
const JOB_DESCRIPTION_FILE_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];

function isAllowedJobDescriptionFile(file) {
  if (!file) return false;

  const name = String(file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
  if (JOB_DESCRIPTION_FILE_EXTENSIONS.includes(ext)) return true;

  return !!file.type && JOB_DESCRIPTION_FILE_MIME_TYPES.includes(file.type);
}


export default function JobInfoModal({
  jobId,
  company,
  adminPerms,
  updateSpinner,
  onClose,
  className = '',
  isDeleting = false
}) {
  const [originalJob, setOriginalJob] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [job, setJob] = useState(null);
  const [jobsData, setJobsData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [jobChanged, setJobChanged] = useState(false);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [isSysAdmin, setIsSysAdmin] = useState(false);
  const [canEditJobs, setCanEditJobs] = useState(false);
  const [canCreateSites, setCanCreateSites] = useState(false);
  const [canSetJobDescription, setCanSetJobDescription] = useState(false);
  const [canApproveDescriptions, setCanApproveDescriptions] = useState(false);
  const titleInputRef = useRef(null);
  const hasAutoFocusedTitleRef = useRef(false);
  const initializedSitesForJobIdRef = useRef(null);
  const initializedRolesForJobIdRef = useRef(null);
  const initializedDepartmentsForJobIdRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHistoryDragOver, setIsHistoryDragOver] = useState(false);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [showJobDescriptionModal, setShowJobDescriptionModal] = useState(false);
  const [editSiteId, setEditSiteId] = useState(null);
  const [companyJobData, setCompanyJobData] = useState(null);
  const { t } = useI18n();

  useEffect(() => {
    if (job) {
      let changed = isJobChanged();
      setJobChanged(changed);
      setSaveEnabled(changed);
    }
  }, [job, originalJob, selectedSites, selectedRoles, selectedDepartments, selectedFile]);

  useEffect(() => {
    async function fetchJob() {
      if (jobId >= 0 && company.id) {
        if (updateSpinner) updateSpinner(true);

        // Set up user permissions
        const uniquePermIds = readPermIdsFromSession();
        const effectivePerms = (adminPerms && adminPerms.length > 0) ? adminPerms : uniquePermIds;
        setIsSysAdmin(effectivePerms.includes(Enums.ROLE_PERMS.SystemAdmin));
        setCanEditJobs(
          effectivePerms.includes(Enums.ROLE_PERMS.EditJobs) ||
          effectivePerms.includes(Enums.ROLE_PERMS.SystemAdmin)
        );
        setCanCreateSites(
          effectivePerms.includes(Enums.ROLE_PERMS.CreateSite) ||
          effectivePerms.includes(Enums.ROLE_PERMS.SystemAdmin)
        );
        setCanSetJobDescription(
          effectivePerms.includes(Enums.ROLE_PERMS.SetJobDescription) ||
          effectivePerms.includes(Enums.ROLE_PERMS.SystemAdmin)
        );
        setCanApproveDescriptions(
          effectivePerms.includes(Enums.ROLE_PERMS.ApproveJobDescription) ||
          effectivePerms.includes(Enums.ROLE_PERMS.SystemAdmin)
        );

        // Fetch specific job if editing existing job
        if (jobId && jobId > 0) {
          const jobResp = await RightTimeApi.getAsync(`/Job/${jobId}`);
          if (updateSpinner) updateSpinner(false);

          if (jobResp && jobResp.ok) {
            const { sites, ...jobData } = jobResp.jsonData;
            setJob(jobData);
            setOriginalJob(jobData);
          } else {
            setJob(null);
            setOriginalJob(null);
          }
        } else if (jobId === 0) {
          if (updateSpinner) updateSpinner(false);
          const newJob = {
            id: 0,
            companyid: company.id,
            companyName: '',
            jobName: '',
            description: '',
            supervisorJobId: null,
            departmentIds: [],
            active: true,
            stateId: Enums.JOB_STATE.PendingDescription
          };
          setJob(newJob);
          setOriginalJob(newJob);
        } else {
          if (updateSpinner) updateSpinner(false);
          setJob(null);
          setOriginalJob(null);
        }
      } else {
        setJob(null);
        setOriginalJob(null);
      }
    }
    setJobsData(null);

    fetchJob();
  }, [jobId, company.id]);

  useEffect(() => {
    hasAutoFocusedTitleRef.current = false;
  }, [jobId, company.id]);

  async function fetchSitesData() {
    if (company.id) {
      try {
        const resp = await fetchCompanyJobsTabMerged(company.id);
        if (resp && resp.ok && resp.jsonData) {
          setCompanyJobData(resp.jsonData);
        }
      } catch (error) {
        console.error("Error fetching sites data:", error);
      }
    }
  }

  useEffect(() => {
    if (!job) return;

    fetchSitesData();

    if (initializedSitesForJobIdRef.current !== job.id) {
      setSelectedSites(Array.isArray(job.siteIds) ? job.siteIds : []);
      initializedSitesForJobIdRef.current = job.id;
    }

    if (initializedRolesForJobIdRef.current !== job.id) {
      setSelectedRoles(Array.isArray(job.jobRoles) ? job.jobRoles : []);
      initializedRolesForJobIdRef.current = job.id;
    }

    if (initializedDepartmentsForJobIdRef.current !== job.id) {
      setSelectedDepartments(Array.isArray(job.departmentIds) ? job.departmentIds : []);
      initializedDepartmentsForJobIdRef.current = job.id;
    }
  }, [job?.id, company.id]);

  function handleSiteToggle(siteId) {
    setSelectedSites(prev => {
      if (prev.includes(siteId)) {
        return prev.filter(id => id !== siteId);
      } else {
        return [...prev, siteId];
      }
    });
  }

  function handleRoleToggle(roleId) {
    setSelectedRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  }

  function handleDepartmentToggle(departmentId) {
    setSelectedDepartments(prev => {
      if (prev.includes(departmentId)) {
        return prev.filter(id => id !== departmentId);
      }
      return [...prev, departmentId];
    });
  }

  function toggleAllDepartmentsAccess() {
    const allDepartmentIds = activeDepartments.map((department) => department.id);
    if (allDepartmentIds.length === 0) return;

    const selectedSet = new Set(selectedDepartments || []);
    const checkedCount = allDepartmentIds.filter((id) => selectedSet.has(id)).length;
    const uncheckedCount = allDepartmentIds.length - checkedCount;
    const shouldUncheckAll = checkedCount > uncheckedCount;

    setSelectedDepartments(shouldUncheckAll ? [] : allDepartmentIds);
  }

  const activeDepartments = (companyJobData?.departments ?? []).filter((d) => d.active !== false);

  function toggleAllSitesAccess() {
    const allSiteIds = (companyJobData?.sites || []).map((site) => site.id);
    if (allSiteIds.length === 0) return;

    const selectedSet = new Set(selectedSites || []);
    const checkedCount = allSiteIds.filter((id) => selectedSet.has(id)).length;
    const uncheckedCount = allSiteIds.length - checkedCount;
    const shouldUncheckAll = checkedCount > uncheckedCount;

    setSelectedSites(shouldUncheckAll ? [] : allSiteIds);
  }

  function toggleAllRolesAccess() {
    const allRoleIds = (companyJobData?.roles || []).map((role) => role.roleId ?? role.id).filter((id) => id !== undefined && id !== null);
    if (allRoleIds.length === 0) return;

    const selectedSet = new Set(selectedRoles || []);
    const checkedCount = allRoleIds.filter((id) => selectedSet.has(id)).length;
    const uncheckedCount = allRoleIds.length - checkedCount;
    const shouldUncheckAll = checkedCount > uncheckedCount;

    setSelectedRoles(shouldUncheckAll ? [] : allRoleIds);
  }



  // Separate function to fetch company jobs when needed
  async function fetchCompanyJobs() {
    if (!company.id || jobsData) return;

    if (updateSpinner) updateSpinner(true);
    try {
      const jobsResp = await RightTimeApi.getAsync(`/Company/${company.id}/Jobs`);
      if (jobsResp && jobsResp.ok) {
        setJobsData(jobsResp.jsonData);
      } else {
        setJobsData([]);
      }
    } catch (error) {
      console.error('Error fetching company jobs:', error);
      setJobsData(null);
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  // Focus the title input when editing a new job
  useEffect(() => {
    if (job && job.id === 0 && canEditJobs && titleInputRef.current && !hasAutoFocusedTitleRef.current) {
      // Small delay to ensure the modal is fully rendered
      const timeoutId = setTimeout(() => {
        titleInputRef.current.focus();
      }, 100);
      hasAutoFocusedTitleRef.current = true;
      return () => clearTimeout(timeoutId);
    }
  }, [job?.id, canEditJobs]);

  if (!job) return null;

  function handleDeleteClick() {
    setShowDeleteConfirm(true);
  }
  function handleDeleteCancel() {
    setShowDeleteConfirm(false);
  }
  function isValid() {
    if (!job) return false;
    if (!job.name?.trim()) return false;
    return true;
  }

  async function handleDeleteConfirm() {
    setShowDeleteConfirm(false);
    if (!job || !job.id) return;
    if (updateSpinner) updateSpinner(true);
    let resp = await RightTimeApi.deleteAsync(`/Job/${job.id}`);
    if (updateSpinner) updateSpinner(false);
    if (resp && resp.ok) {
      toast.success("Job deleted.");
      if (onClose) onClose();
    } else {
      toast.error("Error deleting job.");
    }
  }

  async function handleSave() {
    if (!job) return;

    const missingFields = [];
    if (!job.name?.trim()) {
      missingFields.push("Title");
    }
    if (job.id === 0 && selectedSites.length === 0) {
      missingFields.push(`At least one ${company?.subEntityTypeName || "Location"}`);
    }

    if (missingFields.length > 0) {
      toast.error(`Missing required fields: ${missingFields.join(", ")}.`);
      return;
    }

    if (updateSpinner) updateSpinner(true);

    // Use JSON payload for job data
    const payload = {
      id: job.id,
      companyid: company.id,
      companyName: job.companyName,
      name: job.name,
      description: job.description,
      supervisorJobId: job.supervisorJobId,
      stateId: Enums.JOB_STATE.PendingDescription,
      active: job.active ?? true,
      culturalCompanyTestTypeId: job.culturalCompanyTestTypeId,
      departmentIds: selectedDepartments,
      siteIds: selectedSites,
      jobRoles: selectedRoles
    };
    const resp = await RightTimeApi.sendAsync("/CompanyJob", payload);

    if (resp && resp.ok) {
      const jobId = job.id || resp.jsonData.id;
      const isNewJob = job.id === 0;
      toast.success(isNewJob ? "Job created." : "Job updated.");

      // Update job sites
      // await updateJobSites();

      // Upload file if selected
      if (selectedFile) {
        if (!rejectInvalidJobDescriptionFile(selectedFile)) {
          if (updateSpinner) updateSpinner(false);
          return;
        }
        try {
            // Now upload the file to the description
            const fileResp = await RightTimeApi.postFileAsync(`/Job/${jobId}/File`, selectedFile);

            if (fileResp && fileResp.ok) {
              toast.success(isNewJob ? "Job created and file uploaded successfully!" : "File uploaded successfully!");
            } else {
              toast.warning(isNewJob ? "Job created, but file upload failed." : "File upload failed.");
            }
          // } else {
          //   toast.warning("Job created, but failed to create description for file upload.");
          // }
        } catch (error) {
          console.error('Error uploading file:', error);
          toast.warning(isNewJob ? "Job created, but file upload failed." : "File upload failed.");
        }
      }

      if (onClose) onClose({ savedJobId: jobId });
    } else {
      toast.error("Error saving job.");
    }

    if (updateSpinner) updateSpinner(false);
    setSaveEnabled(false);
  }

  async function updateJobSites() {
    if (!job || job.id === 0 || selectedSites.length === 0) return;
    
    try {
      if (updateSpinner) updateSpinner(true);
      const resp = await RightTimeApi.putAsync("/JobToggleRelations", {
        jobId: job.id,
        siteIds: selectedSites,
        companyid: company.id
      });
      if (updateSpinner) updateSpinner(false);
      if (resp && resp.ok) {
        await fetchSitesData();
      }
    } catch (error) {
      if (updateSpinner) updateSpinner(false);
      console.error("Error updating job sites:", error);
    }
  }

  function isJobChanged() {
    if (!originalJob || !job) return false;
    // Compare relevant fields, treating undefined and empty string as the same
    const fieldChanged = (
      (job.name || '') !== (originalJob.name || '') ||
      (job.description || '') !== (originalJob.description || '') ||
      job.supervisorJobId !== originalJob.supervisorJobId ||
      job.stateId !== originalJob.stateId ||
      job.active !== originalJob.active ||
      (job.companyName || '') !== (originalJob.companyName || '') ||
      job.culturalCompanyTestTypeId !== originalJob.culturalCompanyTestTypeId
    );

    // Consider file selection as a change
    const fileSelected = selectedFile !== null;

    const originalSites = Array.isArray(originalJob.siteIds) ? originalJob.siteIds : [];
    const currentSites = Array.isArray(selectedSites) ? selectedSites : [];
    if (originalSites.length !== currentSites.length) return true;
    const originalSet = new Set(originalSites);
    const sitesChanged = currentSites.some((id) => !originalSet.has(id));

    const originalRoles = Array.isArray(originalJob.jobRoles) ? originalJob.jobRoles : [];
    const currentRoles = Array.isArray(selectedRoles) ? selectedRoles : [];
    if (originalRoles.length !== currentRoles.length) return true;
    const originalRolesSet = new Set(originalRoles);
    const rolesChanged = currentRoles.some((id) => !originalRolesSet.has(id));

    const originalDepartments = Array.isArray(originalJob.departmentIds) ? originalJob.departmentIds : [];
    const currentDepartments = Array.isArray(selectedDepartments) ? selectedDepartments : [];
    if (originalDepartments.length !== currentDepartments.length) return true;
    const originalDepartmentsSet = new Set(originalDepartments);
    const departmentsChanged = currentDepartments.some((id) => !originalDepartmentsSet.has(id));

    return fieldChanged || fileSelected || sitesChanged || rolesChanged || departmentsChanged;
  }


  const footer = (
    <>
      <div className="footerLeft">
        {job.id !== 0 && canApproveDescriptions && (
          <button
            type="button"
            className="btn btn-danger iconButton"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            title={isDeleting ? t('Deleting...') : t('Delete Job')}
          >
            {isDeleting ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <IoTrashOutline />
            )}
          </button>
        )}
      </div>
      <div className="footerRight">
        <button
          type="button"
          // className={`btn btn-primary${saveEnabled ? '' : ' disabled'}`}
          onClick={handleSave}
          disabled={!canEditJobs || (!saveEnabled && !jobChanged)}
        >{t('Save')}
        </button>
      </div>
    </>
  );

  function handleJobPropertyChange(field, value) {
    setJob(prev => ({
      ...prev,
      [field]: field === 'supervisorJobId'
        ? (value === '' || value == null ? null : parseInt(value, 10))
        : value
    }));
  }

  // Check if any description history entries have a description value
  const hasDescriptions = originalJob?.descriptionHistory?.some(entry =>
    (entry.description && entry.description.trim() !== '') ||
    (entry.attachmentId && entry.attachmentFileName)
  ) || false;

  // Check if any description history entries have a notes value
  const hasNotes = originalJob?.descriptionHistory?.some(entry =>
    entry.notes && entry.notes.trim() !== ''
  ) || false;

  const allowDescriptionDrop = canEditJobs && (job.id === 0 || !hasDescriptions);

  function rejectInvalidJobDescriptionFile(file) {
    if (isAllowedJobDescriptionFile(file)) return true;
    toast.error(t("Only PDF, DOCX, and TXT files are allowed for job descriptions.", "Only PDF, DOCX, and TXT files are allowed for job descriptions."));
    return false;
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!rejectInvalidJobDescriptionFile(file)) {
      e.target.value = "";
      return;
    }
    setSelectedFile(file);
  }

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!allowDescriptionDrop) return;

    const file = e.dataTransfer?.files?.[0];
    if (file && !isAllowedJobDescriptionFile(file)) {
      e.dataTransfer.dropEffect = "none";
      setIsDragOver(false);
      return;
    }

    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragOver to false if we're leaving the modal container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (!allowDescriptionDrop) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!rejectInvalidJobDescriptionFile(file)) return;
      setSelectedFile(file);
      toast.success(`File "${file.name}" ready for upload`);
    }
  };

  const handleDescriptionFileDrop = async (e, descriptionId) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (!canEditJobs) return;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!rejectInvalidJobDescriptionFile(file)) return;
    if (job.id === 0) {
      setSelectedFile(file);
      toast.success(`File "${file.name}" ready for upload`);
      return;
    }

    if (descriptionId) {
      await uploadFileToDescription(descriptionId, file);
    } else {
      setSelectedFile(file);
      toast.success(`File "${file.name}" ready for upload`);
    }
  };

  const handleDescriptionHistoryDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEditJobs || !job?.id) return;

    const file = e.dataTransfer?.files?.[0];
    if (file && !isAllowedJobDescriptionFile(file)) {
      e.dataTransfer.dropEffect = "none";
      setIsHistoryDragOver(false);
      return;
    }

    e.dataTransfer.dropEffect = "copy";
    setIsHistoryDragOver(true);
  };

  const handleDescriptionHistoryDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsHistoryDragOver(false);
    }
  };

  const handleDescriptionHistoryDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsHistoryDragOver(false);

    if (!canEditJobs || !job?.id) return;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!rejectInvalidJobDescriptionFile(file)) return;

    try {
      if (updateSpinner) updateSpinner(true);

      const fileResp = await RightTimeApi.postFileAsync(`/Job/${job.id}/Description/File`, file);

      if (fileResp && fileResp.ok) {
        toast.success("Description file saved successfully!");
        const jobResp = await RightTimeApi.getAsync(`/Job/${job.id}`);
        if (jobResp && jobResp.ok) {
          setJob(jobResp.jsonData);
          setOriginalJob(jobResp.jsonData);
        }
      } else {
        toast.error("Failed to save description file.");
      }
    } catch (error) {
      console.error('Error saving description file:', error);
      toast.error("Error saving description file.");
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  };

  function handleDownloadAttachment(entry) {
    if (entry.attachmentFileName && entry.attachmentId && job.id) {
      Helpers.downloadJobDescriptionAttachmentToFolder(job.id, entry);
    }
  }

  const fields = {};
  fields[t('Title')] = canEditJobs ? (
    <input
      ref={titleInputRef}
      type="text"
      className="titleInput"
      value={job.name || ''}
      onChange={(e) => handleJobPropertyChange('name', e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSave();
        }
      }}
    />
  ) : (
    <span>{job.name || ''}</span>
  );

  fields[t('Active')] = canEditJobs ? (
    <input
      type="checkbox"
      className="activeCheckbox"
      checked={job.active ?? true}
      onChange={(e) => handleJobPropertyChange('active', e.target.checked)}
    />
  ) : (
    <input type="checkbox" checked={job.active ?? true} readOnly disabled />
  );

  let mostRecentDesc = null;

  // Add Description field for job states 10 (PendingEF) and 30 (Active)
  if (job.stateId === Enums.JOB_STATE.PendingEF || job.stateId === Enums.JOB_STATE.Active) {
    // Get most recent description from descriptionHistory
    const getMostRecentFromHistory = () => {
      if (!originalJob?.descriptionHistory || originalJob.descriptionHistory.length === 0) {
        return null;
      }

      const validDescriptions = originalJob.descriptionHistory
        .filter(d => d.stateId !== Enums.JOB_DESCRIPTION_STATE.Deleted && d.stateId !== Enums.JOB_DESCRIPTION_STATE.Archived)
        .sort((a, b) => {
          // Sort by createdTime, newest first
          try {
            const aDt = DateTime.fromISO(a.createdTime);
            const bDt = DateTime.fromISO(b.createdTime);
            if (aDt.isValid && bDt.isValid) {
              return bDt.toMillis() - aDt.toMillis();
            }
          } catch { }
          const aMs = new Date(a.createdTime).getTime();
          const bMs = new Date(b.createdTime).getTime();
          return bMs - aMs;
        });

      return validDescriptions.length > 0 ? validDescriptions[0] : null;
    };

    mostRecentDesc = getMostRecentFromHistory();
    fields[t('Description')] = (
      <textarea
        value={job.description || ''}
        onChange={(e) => handleJobPropertyChange('description', e.target.value)}
        rows={6}
        placeholder={t('Paste or type job description...')}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => handleDescriptionFileDrop(e, mostRecentDesc?.id)}
        style={{ width: '100%', resize: 'vertical' }}
      />
    );
  }

  // Add file upload field for new jobs or when no descriptions exist
  if ((job.id === 0 || !hasDescriptions) && canEditJobs) {
    if (selectedFile) {
      fields[t('Job Description')] = (
        <div 
          style={{
            padding: '12px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
          onClick={() => setSelectedFile(null)}
          title={t('Click to remove file and enter description manually')}
        >
          <span style={{ fontWeight: '500' }}>{selectedFile.name}</span>
          <IoTrashOutline className="actionIcon" style={{ fontSize: '18px', color: '#6c757d', margin: '0 8px 0 20px' }} />
        </div>
      );
    } else {
      fields[t('Job Description')] = (
        <textarea
          value={job.description || ''}
          onChange={(e) => handleJobPropertyChange('description', e.target.value)}
          rows={6}
          className="descriptionTextarea"
          placeholder={t('Paste or type job description... (or drag a file here)')}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => handleDescriptionFileDrop(e, mostRecentDesc?.id)}
          style={{ width: '100%', resize: 'vertical' }}
        />
      );
    }
  }
  function onClickAttachment(e) {
    const entry = e.currentTarget.dataset.id ? originalJob.descriptionHistory.find(d => d.id === parseInt(e.currentTarget.dataset.id)) : null;
    if (entry.attachmentId && entry.attachmentFileName)
      handleDownloadAttachment(entry);
    else {
      // Prompt user to select a file to attach
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = ".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain";
      fileInput.onchange = async (event) => {
        const target = event.target as HTMLInputElement;
        const file = target?.files?.[0];
        if (file && entry) {
          if (!rejectInvalidJobDescriptionFile(file)) return;
          await uploadFileToDescription(entry.id, file);
        }
      };
      fileInput.click();
    }
  }

  async function uploadFileToDescription(descriptionId, file) {
    if (!job.id || !file) {
      toast.error("Missing required information for file upload.");
      return;
    }
    if (!rejectInvalidJobDescriptionFile(file)) return;

    try {
      if (updateSpinner) updateSpinner(true);
      const fileResp = await RightTimeApi.postFileAsync(`/Job/${job.id}/File`, file);

      if (fileResp && fileResp.ok) {
        toast.success("File uploaded successfully!");
        // Reload the job data to show the new attachment
        const jobResp = await RightTimeApi.getAsync(`/Job/${job.id}`);
        if (jobResp && jobResp.ok) {
          setJob(jobResp.jsonData);
          setOriginalJob(jobResp.jsonData);
        }
      } else {
        toast.error("Failed to upload file.");
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error("Error uploading file.");
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }


  const modalTitle = job.id === 0 ? t('Add Job') : `${t('Job Info')} (${job.id})`;

  return (
    <>
      <Modal
        isOpen={true}
        title={modalTitle}
        onClose={onClose}
        className={`jobInfoModal ${className} ${isDragOver && allowDescriptionDrop ? 'drag-over' : ''}`}
        updateSpinner={updateSpinner}
        footer={footer}
      >
        <div
          className={`modal-drop-zone ${isDragOver && allowDescriptionDrop ? 'drag-active' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            position: 'relative',
            minHeight: '100%',
            border: isDragOver && allowDescriptionDrop ? '2px dashed #007bff' : '2px dashed transparent',
            borderRadius: '8px',
            transition: 'border-color 0.2s ease',
            padding: isDragOver && allowDescriptionDrop ? '10px' : '0px'
          }}
        >
          {isDragOver && allowDescriptionDrop && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 123, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                zIndex: 1000,
                pointerEvents: 'none'
              }}
            >
              <div style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                textAlign: 'center',
                border: '2px solid #007bff'
              }}>
                <FaFileUpload size={48} color="#007bff" style={{ marginBottom: '10px' }} />
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
                  Drop job description file here
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                  {t("PDF or DOCX only", "PDF or DOCX only")}
                </div>
              </div>
            </div>
          )}
          <ModalFormFields fields={fields} />
          
          {job.id > 0 && hasDescriptions && job.stateId !== Enums.JOB_STATE.PendingEF && job.stateId !== Enums.JOB_STATE.Active && (
            <div
              className="tableWrapper dimensionTable scrollable"
              onDragOver={handleDescriptionHistoryDragOver}
              onDragLeave={handleDescriptionHistoryDragLeave}
              onDrop={handleDescriptionHistoryDrop}
              style={{
                border: isHistoryDragOver ? '2px dashed #007bff' : undefined,
                borderRadius: isHistoryDragOver ? '8px' : undefined,
                padding: isHistoryDragOver ? '6px' : undefined
              }}
            >
              <table>
                <caption><span className="title">{t('Description History')}</span>
                  <FaPlusSquare
                    className={`actionIcon plusIcon captionRight${canSetJobDescription ? "" : " disabled"}`}
                    onClick={() => {
                      if (canSetJobDescription) {
                        setShowJobDescriptionModal(true);
                      }
                    }}
                    title={canSetJobDescription ? t('Add Job Description') : t("You don't have permission to add job descriptions")}
                  />
                </caption>
                <thead>
                  <tr>
                    <th>{t('Date Created')}</th>
                    <th>{t('Job Description')}</th>
                    {hasNotes && <th>{t('Notes')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {originalJob?.descriptionHistory && originalJob.descriptionHistory
                    .sort((a, b) => {
                      try {
                        const aTime = DateTime.fromISO(a.createdTime);
                        const bTime = DateTime.fromISO(b.createdTime);
                        if (aTime.isValid && bTime.isValid) {
                          return bTime.toMillis() - aTime.toMillis();
                        }
                      } catch { }
                      const aMs = new Date(a.createdTime).getTime();
                      const bMs = new Date(b.createdTime).getTime();
                      return bMs - aMs;
                    })
                    .map((entry, index) => (
                    <tr key={entry.id}>
                      <td>{DateTime.fromISO(entry.createdTime, { zone: "America/New_York" }).toFormat("MM/dd/yyyy")}</td>
                      <td
                        style={{
                          cursor: entry.attachmentFileName && entry.attachmentId ? 'pointer' : 'default',
                          color: entry.attachmentFileName && entry.attachmentId ? '#007bff' : 'inherit',
                          textDecoration: entry.attachmentFileName && entry.attachmentId ? 'underline' : 'none'
                        }}
                        data-id={entry.id}
                        onClick={entry.attachmentFileName && entry.attachmentId ? onClickAttachment : undefined}
                      >
                        {entry.attachmentFileName && entry.attachmentId ? (
                          entry.attachmentFileName
                        ) : (
                          entry.description || '-'
                        )}
                      </td>
                      {hasNotes && <td>{entry.notes}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {job && (
            <div>
              {company?.departmentsEnabled && activeDepartments.length > 0 ? (
                <div className="tableWrapper dimensionTable jobDepartmentsTable">
                  <table>
                    <caption>
                      <span className="title">{t('Departments')}</span>
                    </caption>
                    <thead>
                      <tr>
                        <th>{t('Department')}</th>
                        <th
                          className="toggleColumnHeader"
                          onClick={canEditJobs ? toggleAllDepartmentsAccess : undefined}
                          title={canEditJobs ? t('Toggle all access') : undefined}
                        >
                          <span className="toggleHeaderLabel">{t('Assigned')}</span>
                          {canEditJobs ? (
                            <span className="toggleHeaderIcon" aria-hidden="true">
                              <MdCameraswitch />
                            </span>
                          ) : null}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeDepartments.map((department) => {
                        const isAssigned = selectedDepartments.includes(department.id);
                        return (
                          <tr key={`dept-${department.id}`}>
                            <td>{department.name}</td>
                            <td
                              className="actions"
                              onClick={() => {
                                if (canEditJobs) handleDepartmentToggle(department.id);
                              }}
                            >
                              {isAssigned ? (
                                <MdCheckBox className={`checkbox checked actionIcon${canEditJobs ? "" : " disabled"}`} />
                              ) : (
                                <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canEditJobs ? "" : " disabled"}`} />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
                <div className="tableWrapper dimensionTable jobSitesTable">
                  <table>
                    <caption>
                      <span className="title">{t('{entityName}', '{entityName}', { entityName: company?.subEntityTypeName || 'Location' })}</span>
                      <FaPlusSquare
                        className={`actionIcon plusIcon captionRight${canCreateSites ? "" : " disabled"}`}
                        onClick={() => {
                          if (canCreateSites) {
                            setEditSiteId(0);
                          }
                        }}
                        title={canCreateSites ? t('Add {entityName}', 'Add {entityName}', { entityName: company?.subEntityTypeName || 'Location' }) : t("You don't have permission to add locations")}
                      />
                    </caption>
                    <thead>
                      <tr>
                        <th>{t('{entityName}', '{entityName}', { entityName: company?.subEntityTypeName || 'Location' })}</th>
                        <th
                          className="toggleColumnHeader"
                          onClick={toggleAllSitesAccess}
                          style={{ cursor: 'pointer' }}
                          title={t('Toggle all access')}
                        >
                          <span className="toggleHeaderLabel">{t('Access')}</span>
                          <span className="toggleHeaderIcon" aria-hidden="true">
                            <MdCameraswitch />
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyJobData?.sites?.map((site) => {
                        const isAssociated = selectedSites.includes(site.id);
                        return (
                          <tr key={"site" + site.id}>
                            <td>{site.alias || site.name || ""}</td>
                            <td
                              className="actions"
                              onClick={() => handleSiteToggle(site.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              {isAssociated ? (
                                <MdCheckBox className="checkbox checked actionIcon" />
                              ) : (
                                <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              {isSysAdmin && companyJobData?.roles?.length > 0 && (
                <div className="tableWrapper dimensionTable jobRolesTable">
                  <table>
                    <caption>
                      <span className="title">{t('Roles')}</span>
                    </caption>
                    <thead>
                      <tr>
                        <th>{t('Role')}</th>
                        <th
                          className="toggleColumnHeader"
                          onClick={toggleAllRolesAccess}
                          style={{ cursor: 'pointer' }}
                          title={t('Toggle all access')}
                        >
                          <span className="toggleHeaderLabel">{t('Access')}</span>
                          <span className="toggleHeaderIcon" aria-hidden="true">
                            <MdCameraswitch />
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyJobData.roles.map((role) => {
                        const roleId = role.roleId ?? role.id;
                        const isAssociated = selectedRoles.includes(roleId);
                        return (
                          <tr key={"role" + roleId}>
                            <td>{role.name || role.display || ""}</td>
                            <td
                              className="actions"
                              onClick={() => handleRoleToggle(roleId)}
                              style={{ cursor: 'pointer' }}
                            >
                              {isAssociated ? (
                                <MdCheckBox className="checkbox checked actionIcon" />
                              ) : (
                                <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title={t('Delete Job')}
        message={t('Are you sure you want to delete job "{jobName}"?', 'Are you sure you want to delete job "{jobName}"?', { jobName: job.name })}
        confirmText={t('Yes, Delete Job')}
        dangerText={t('This will permanently remove the job and all associated data.')}
        showSecondStep={false}
        secondStepMessage={t('Delete {jobName} permanently?', 'Delete {jobName} permanently?', { jobName: job.name })}
        secondStepWarning={t('This job and all its data will be permanently removed from the system.')}
        secondStepDetail={t('This includes assignments, history, and related records. This action cannot be undone.')}
      />
      {showJobDescriptionModal && (
        <JobDescriptionModal
          jobId={job.id}
          jobDescriptionId={null}
          updateSpinner={updateSpinner}
          onClose={() => {
            setShowJobDescriptionModal(false);
            // Refresh the job data to show new description
            if (job.id > 0) {
              RightTimeApi.getAsync(`/Job/${job.id}`).then((resp) => {
                if (resp && resp.ok) {
                  setJob(resp.jsonData);
                  setOriginalJob(resp.jsonData);
                }
              });
            }
          }}
        />
      )}
      {editSiteId !== null && (
        <EditSiteModal
          isOpen={editSiteId !== null}
          onClose={async (savedSite) => {
            const wasNewSite = editSiteId === 0;
            const savedSiteId = savedSite?.siteId || savedSite?.id;
            setEditSiteId(null);
            if (wasNewSite && savedSiteId) {
              setSelectedSites((prev) => (prev.includes(savedSiteId) ? prev : [...prev, savedSiteId]));
            }
            await fetchSitesData();
          }}
          siteId={editSiteId}
          company={company}
          updateSpinner={updateSpinner}
          jobs={
            editSiteId === 0 && job?.id > 0
              ? [{ id: job.id, value: job.name || job.jobName || "" }]
              : editSiteId === 0
                ? []
                : companyJobData.jobs
          }
          defaultJobIds={editSiteId === 0 && job?.id > 0 ? [job.id] : []}
          adminPerms={adminPerms}
        />
      )}
    </>
  );
}


